import { useState } from "react";
import { toast } from "sonner";
import { Loader2, HeartHandshake, ShieldCheck } from "lucide-react";
import { PageHeader, SectionLabel } from "@/components/shared";
import { Reveal } from "@/components/motion/Reveal";
import { Checkbox } from "@/components/ui/checkbox";
import { postPrayer } from "@/lib/api";
import { ASSETS } from "@/lib/assets";

export default function Prayer() {
  const [form, setForm] = useState({ name: "", email: "", request: "", is_private: false });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.request) return;
    setLoading(true);
    try {
      const payload = { ...form, email: form.email || undefined };
      const res = await postPrayer(payload);
      toast.success(res.message || "We're praying with you.");
      setForm({ name: "", email: "", request: "", is_private: false });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-wutn py-3 text-white placeholder:text-zinc-500 outline-none transition-colors";

  return (
    <div data-testid="prayer-page">
      <PageHeader
        testId="prayer-header"
        label="We Stand With You"
        title={<>Prayer <span className="text-wutn">Requests</span></>}
        subtitle="Nothing is too big or too small. Share your request and our team will lift it before God. You are not alone."
        image={ASSETS.preachingEvent2}
      />

      <section className="bg-black py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionLabel>Why We Pray</SectionLabel>
            <h2 className="mt-5 font-display uppercase text-4xl md:text-5xl leading-[0.9]">Hunger & <span className="text-wutn">Desperation</span></h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
              We believe prayer moves the hand of God. Our intercessors take every request seriously and confidentially, contending until heaven answers.
            </p>
            <div className="mt-10 space-y-5">
              <div className="flex gap-4">
                <ShieldCheck className="h-6 w-6 text-wutn shrink-0" />
                <div>
                  <h4 className="font-cond uppercase text-white">Kept Confidential</h4>
                  <p className="text-sm text-zinc-400">Mark your request private and it stays between you and our prayer team.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <HeartHandshake className="h-6 w-6 text-wutn shrink-0" />
                <div>
                  <h4 className="font-cond uppercase text-white">Prayed Over Personally</h4>
                  <p className="text-sm text-zinc-400">Every request reaches a real person who will pray with faith on your behalf.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <form onSubmit={submit} data-testid="prayer-form" className="bg-[#0A0A0A] border border-white/10 p-8 md:p-10 space-y-7">
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">Your Name *</label>
                  <input required value={form.name} onChange={set("name")} data-testid="prayer-name" className={inputCls} placeholder="Full name" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">Email (optional)</label>
                  <input type="email" value={form.email} onChange={set("email")} data-testid="prayer-email" className={inputCls} placeholder="So we can follow up" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">Your Request *</label>
                  <textarea required rows={5} value={form.request} onChange={set("request")} data-testid="prayer-request" className={`${inputCls} resize-none`} placeholder="Share what's on your heart..." />
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox checked={form.is_private} onCheckedChange={(v) => setForm((f) => ({ ...f, is_private: !!v }))} data-testid="prayer-private" className="border-white/30 data-[state=checked]:bg-wutn data-[state=checked]:border-wutn rounded-none" />
                  <span className="text-sm text-zinc-400">Keep this request private</span>
                </label>
                <button type="submit" disabled={loading} data-testid="prayer-submit" className="w-full inline-flex items-center justify-center gap-2 bg-wutn hover:bg-wutn-dark disabled:opacity-60 text-white px-6 py-4 font-cond uppercase tracking-wider text-sm transition-colors">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <HeartHandshake className="h-4 w-4" />} Submit Request
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
